import { Download, FileSpreadsheet } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

export interface TemplateDownloadCardProps {
  corridor: { country: string; rail: string } | null;
  onDownload: (country: string, rail: string) => void;
  isDownloading: boolean;
  errorMessage: string | null;
}

/** The corridor's template: one payment per row, the amount being what the beneficiary receives. */
export function TemplateDownloadCard({
  corridor,
  onDownload,
  isDownloading,
  errorMessage,
}: TemplateDownloadCardProps) {
  const { t } = useTranslations("payoutBatches");

  return (
    <ColocaSurface variant="muted" padding="md" className="flex h-full flex-col gap-4">
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileSpreadsheet aria-hidden="true" className="size-5" />
        </span>
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-foreground">
            {corridor ? t("template_title", { rail: formatRail(corridor.rail) }) : t("template_download")}
          </p>
          <p className="text-xs text-muted-foreground">{t("template_description")}</p>
        </div>
      </div>
      <div className="mt-auto space-y-2">
        <ColocaButton
          variant="default"
          size="sm"
          icon={Download}
          loading={isDownloading}
          disabled={!corridor}
          onClick={() => corridor && onDownload(corridor.country, corridor.rail)}
        >
          {isDownloading ? t("template_downloading") : t("template_download")}
        </ColocaButton>
        {!corridor && <p className="text-xs text-muted-foreground">{t("template_need_corridor")}</p>}
        {errorMessage && <p className="text-xs text-destructive">{errorMessage}</p>}
      </div>
    </ColocaSurface>
  );
}
